import app from 'flarum/common/app';
export default class FileListState {
  constructor(sharedFiles) {
    if (sharedFiles === void 0) {
      sharedFiles = false;
    }
    this.user = null;
    this.files = [];
    this.moreResults = false;
    this.loading = false;
    this.loadError = false;
    this.sharedFiles = sharedFiles;
  }

  /**
   * Set the user and load their file list.
   * @param user The user whose files to load.
   */
  setUser(user) {
    if (user === this.user) return;
    this.user = user;
    this.files = [];
    // Same reason as refresh(): a switch to a different user must not be
    // swallowed by the in-flight guard, or the new user's list never loads and
    // they are left looking at an empty one.
    this.loading = false;
    this.loadResults();
  }
  refresh() {
    this.files = [];
    // Clear the in-flight flag so the guard in loadResults() does not turn a
    // deliberate refresh into a no-op. Any response still in flight lands in
    // parseResults, but files has just been emptied, so it cannot duplicate.
    this.loading = false;
    this.loadResults();
    m.redraw();
  }

  /**
   * Load more files for the current user, starting from the given offset.
   * @param offset The starting index for loading more files.
   * @returns A promise resolving to the loaded files.
   */
  async loadResults(offset) {
    if (offset === void 0) {
      offset = 0;
    }
    if (!this.sharedFiles && !this.user) return Promise.reject('User not set');

    // A request is already in flight. Without this, switching between the media
    // tabs remounts the list component and fires a second fetch while the first
    // is still running — parseResults concats both responses, so the list is
    // duplicated on every switch. Guarding here rather than in the component
    // covers loadMore() and refresh() too, which call this directly.
    if (this.loading) return Promise.resolve(this.files);

    // Shared files are loaded once and kept; only paging asks for more.
    if (this.sharedFiles && this.files.length > 0 && offset === 0) {
      return Promise.resolve(this.files);
    }
    this.loading = true;
    this.loadError = false;
    let route = 'fof/uploads';
    let params = {};
    if (!this.sharedFiles && this.user) {
      params = {
        filter: {
          user: this.user.id()
        },
        page: {
          offset
        }
      };
    } else {
      route = 'fof/upload/shared-files';
      params = {
        page: {
          offset
        }
      };
    }
    try {
      const results = await app.store.find(route, params);
      return this.parseResults(results);
    } catch (error) {
      // Reset loading so the in-flight guard above lets a retry through.
      this.loading = false;
      this.loadError = true;
      m.redraw();

      // Deliberately not rethrown: every caller ignores the returned promise,
      // so a rethrow would only produce an unhandled rejection. The error is
      // surfaced through loadError instead, which the list renders — otherwise
      // a failed request is indistinguishable from an empty library.
      return [];
    }
  }

  /**
   * Load the next set of results.
   */
  async loadMore() {
    return this.loadResults(this.files.length);
  }
  parseResults(results) {
    this.files = this.files.concat(results);
    this.loading = false;
    this.moreResults = !!results?.payload?.links?.next;
    m.redraw();
    return results;
  }

  /**
   * Add files to the beginning of the list.
   * @param files The files to be added.
   */
  addToList(files) {
    if (Array.isArray(files)) {
      this.files.unshift(...files);
    } else {
      this.files.unshift(files);
    }
    m.redraw();
  }

  /**
   * Remove files from the list.
   * @param files The files to be removed.
   */
  removeFromList(files) {
    if (Array.isArray(files)) {
      this.files = this.files.filter(file => !files.includes(file));
    } else {
      this.files = this.files.filter(file => file !== files);
    }
    m.redraw();
  }

  /**
   * Check if there are files in the list.
   * @returns True if there are files, false otherwise.
   */
  hasFiles() {
    return this.files.length > 0;
  }

  /**
   * Check if the file list is currently loading.
   * @returns True if loading, false otherwise.
   */
  isLoading() {
    return this.loading;
  }

  /**
   * Whether the last load failed. Lets the list distinguish a failed request
   * from a genuinely empty library.
   */
  hasLoadError() {
    return this.loadError;
  }

  /**
   * Check if there are more files to load.
   * @returns True if there are more files, false otherwise.
   */
  hasMoreResults() {
    return this.moreResults;
  }

  /**
   * Check if the user has no files and the list is not loading.
   * @returns True if the list is empty and not loading, false otherwise.
   */
  empty() {
    return !this.hasFiles() && !this.isLoading();
  }
}
flarum.reg.add('fof-upload', 'common/states/FileListState', FileListState);